import React, { ComponentType } from 'react';
import OriginalImgix, { buildURL } from 'react-imgix';
import type { SharedImgixAndSourceProps } from 'react-imgix';
import { replaceAWSSourceWithImgix } from '../helpers/imgix';

// Type assertion to work around @types/react-imgix incompatibility with React 18 types
const Imgix = OriginalImgix as ComponentType<SharedImgixAndSourceProps>;

/** Imgix parameters extracted from SharedImgixAndSourceProps */
export type ImgixParams = SharedImgixAndSourceProps['imgixParams'];

const DEFAULT_FALLBACK_IMAGE = 'https://images.dutchie.com/category-stock-photos/flower/flower-1.png';

/**
 * Builds a complete Imgix URL with the given source and parameters.
 * Cleans the source URL first to ensure it's valid.
 */
export const buildImgixSrc = (src: string | null | undefined, imgixParams?: ImgixParams): string =>
  buildURL(cleanImgixSrc(src), { ...imgixParams });

/**
 * Cleans and normalizes an image source URL for use with Imgix.
 * - Handles null/undefined/invalid URLs by returning a fallback image
 * - Converts AWS S3 URLs to Imgix URLs
 */
export const cleanImgixSrc = (src: string | null | undefined): string => {
  // Patching: https://github.com/imgix/react-imgix/issues/801
  // This ternary would be hit if a null or broken src was passed - imgixclient throws a hard error.
  // Instead, replace with a default for now.
  const cleanedSrc = src?.includes('://') ? src : DEFAULT_FALLBACK_IMAGE;
  // Replace S3 with Imgix
  return replaceAWSSourceWithImgix(cleanedSrc);
};

/**
 * Builds a srcSet string for responsive images with different DPI multipliers (1x, 2x, 3x).
 */
export const buildImgixSrcSet = (src: string | null | undefined, imgixParams?: ImgixParams): string => {
  // Common DPI multipliers (1x, 2x, 3x)
  const dpiMultipliers = [1, 2, 3];

  return dpiMultipliers
    .map((dpi) => {
      const dpiParams: ImgixParams = {
        ...imgixParams,
        dpr: dpi,
      };

      return `${buildImgixSrc(src, dpiParams)} ${dpi}x`;
    })
    .join(', ');
};

export type ImgixComponentProps = SharedImgixAndSourceProps;

/**
 * A wrapper component around react-imgix that automatically cleans and normalizes image URLs.
 * Handles AWS S3 to Imgix URL conversion and provides a fallback for invalid sources.
 */
const ImgixComponent = ({ src, ...props }: ImgixComponentProps): JSX.Element => {
  const cleanedSrc = cleanImgixSrc(src);
  return <Imgix src={cleanedSrc} {...props} />;
};

export default ImgixComponent;
